import { useQuery } from '@tanstack/preact-query';
import { useContext, useMemo } from 'preact/hooks';
import { ApiClientContext } from '../arcticapi';
import type { LineShape, Stop } from '../arcticapi/types';
import type { MapLine, MapMarker, MapPoint } from '../components/base/Map/Map';
import {
  createRouteLine,
  createRoutePoint,
  resolveRouteColor,
} from '../components/base/Map/routeLineStyle';
import type { TimetableDetailsLine } from './timetableDetailsLine';

export interface UseTimetableMapState {
  loading: boolean;
  error: Error | null;
  unavailableMessage: string | null;
  lines: MapLine[];
  points: MapPoint[];
  markers: MapMarker[];
  fitCoordinates: Array<[number, number]>;
}

export function useTimetableMap(
  selectedLine: TimetableDetailsLine | null,
): UseTimetableMapState {
  const client = useContext(ApiClientContext);
  const shapeHref = selectedLine?.shapeHref ?? null;
  const stopsHref = selectedLine?.stopsHref ?? null;

  const lineShapeQuery = useQuery<LineShape, Error>({
    queryKey: ['line-shape', selectedLine?.id, shapeHref],
    enabled: Boolean(client) && Boolean(shapeHref),
    queryFn: ({ signal }) => {
      if (!client || !shapeHref) {
        throw new Error('Line shape request is missing a shape link.');
      }

      return client.getLineShape(shapeHref, { signal });
    },
  });

  const lineStopsQuery = useQuery<Stop[], Error>({
    queryKey: ['line-stops', selectedLine?.id, stopsHref],
    enabled: Boolean(client) && Boolean(stopsHref),
    queryFn: ({ signal }) => {
      if (!client || !stopsHref) {
        throw new Error('Line stops request is missing a stops link.');
      }

      return client.getLineStops(stopsHref, { signal });
    },
  });

  const lines = useMemo(
    () =>
      mapLineShapeToMapLines(
        lineShapeQuery.data,
        selectedLine?.colors?.background,
        selectedLine?.colors?.foreground,
      ),
    [
      lineShapeQuery.data,
      selectedLine?.colors?.background,
      selectedLine?.colors?.foreground,
    ],
  );

  const points = useMemo(
    () =>
      mapStopsToRoutePoints(
        lineStopsQuery.data ?? [],
        selectedLine?.colors?.background,
        selectedLine?.colors?.foreground,
      ),
    [
      lineStopsQuery.data,
      selectedLine?.colors?.background,
      selectedLine?.colors?.foreground,
    ],
  );

  const markers = useMemo<MapMarker[]>(() => [], []);

  const fitCoordinates = useMemo(() => {
    const coordinates: Array<[number, number]> = [];

    for (const line of lines) {
      coordinates.push(...line.coordinates);
    }

    for (const point of points) {
      coordinates.push(point.coordinate);
    }

    return coordinates;
  }, [lines, points]);

  return {
    loading:
      (Boolean(shapeHref) && lineShapeQuery.isPending) ||
      (Boolean(stopsHref) && lineStopsQuery.isPending),
    error: lineShapeQuery.error ?? lineStopsQuery.error,
    unavailableMessage: getUnavailableMessage(
      selectedLine,
      shapeHref,
      stopsHref,
    ),
    lines,
    points,
    markers,
    fitCoordinates,
  };
}

function mapLineShapeToMapLines(
  shape: LineShape | undefined,
  fallbackColor?: string,
  fallbackForegroundColor?: string,
): MapLine[] {
  if (!shape) {
    return [];
  }

  return shape.features
    .filter(
      (feature) =>
        feature.geometry?.type === 'LineString' &&
        Array.isArray(feature.geometry.coordinates),
    )
    .map((feature, index) =>
      createRouteLine(
        {
          id: toFeatureId(feature.properties, index),
          coordinates: feature.geometry.coordinates,
        },
        resolveRouteColor(
          readLineColor(feature.properties) ?? fallbackColor,
          fallbackForegroundColor,
        ),
      ),
    );
}

function mapStopsToRoutePoints(
  stops: Stop[],
  fallbackColor?: string,
  fallbackForegroundColor?: string,
): MapPoint[] {
  return stops.map((stop) =>
    createRoutePoint(
      {
        id: stop.atcoCode,
        coordinate: stop.location.coordinates,
      },
      resolveRouteColor(fallbackColor, fallbackForegroundColor),
    ),
  );
}

function toFeatureId(
  properties: Record<string, unknown>,
  index: number,
): string {
  const id = properties.id;

  if (typeof id === 'string' && id.trim()) {
    return id;
  }

  return `shape-${index + 1}`;
}

function readLineColor(properties: Record<string, unknown>): string | null {
  const colorKeys = [
    'color',
    'colour',
    'lineColor',
    'lineColour',
    'stroke',
    'background',
  ];

  for (const key of colorKeys) {
    const value = properties[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  return null;
}

function getUnavailableMessage(
  selectedLine: TimetableDetailsLine | null,
  shapeHref: string | null,
  stopsHref: string | null,
): string | null {
  if (!selectedLine) {
    return null;
  }

  if (!shapeHref && !stopsHref) {
    return 'Map data is unavailable for this service.';
  }

  if (!shapeHref) {
    return 'Route geometry is unavailable for this service.';
  }

  if (!stopsHref) {
    return 'Stop locations are unavailable for this service.';
  }

  return null;
}
